import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import type { ShopifyBlogArticle } from '@/lib/shopifyBlog';

interface ArticleCardProps {
  article: ShopifyBlogArticle;
  index?: number;
}

export function ArticleCard({ article, index = 0 }: ArticleCardProps) {
  const { i18n, t } = useTranslation();
  const lang = i18n.language === 'en' ? 'en-GB' : 'it-IT';

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <Link
        to={`/blog/${article.handle}`}
        className="group block rounded-lg overflow-hidden bg-card card-hover"
      >
        {article.image && (
          <div className="aspect-[16/9] overflow-hidden">
            <img
              src={article.image.url}
              alt={article.image.altText || article.title}
              className="w-full h-full object-cover img-zoom"
              loading="lazy"
              width={600}
              height={338}
            />
          </div>
        )}
        <div className="p-5 md:p-6">
          <time
            dateTime={article.publishedAt}
            className="font-body text-xs text-muted-foreground"
          >
            {new Date(article.publishedAt).toLocaleDateString(lang, {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </time>
          <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground mt-2 mb-2 group-hover:text-bronze transition-colors">
            {article.title}
          </h2>
          {article.excerpt && (
            <p className="font-body text-sm text-muted-foreground line-clamp-3 mb-4">
              {article.excerpt}
            </p>
          )}
          <span className="inline-flex items-center gap-1 font-body text-sm font-medium text-bronze group-hover:gap-2 transition-all">
            {t('blog.readMore')}
            <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </Link>
    </motion.article>
  );
}
